import { type ReactElement, type ReactNode } from 'react';

import { twcn } from '@web/ui/core/utils';

/* ────────────────────────── Item ────────────────────────── */

interface SegmentedButtonItemProps {
  label: string;
  /** Optional leading icon — caller renders the SVG inside. */
  icon?: ReactNode;
  /** Trailing mono count chip rendered next to the label. */
  count?: number;
  isActive?: boolean;
  onClick: () => void;
}

function Item({
  label,
  icon,
  count,
  isActive = false,
  onClick,
}: Readonly<SegmentedButtonItemProps>): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      onClick={onClick}
      className={twcn(
        'inline-flex items-center gap-1.25 rounded-sm border-0 bg-transparent px-2.5 py-1 text-xs font-medium transition-colors duration-fast ease-standard',
        isActive
          ? 'bg-surface text-foreground shadow-pill-active'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
      {typeof count === 'number' ? (
        <span
          className={twcn(
            'font-mono text-tiny font-normal tabular-nums',
            isActive ? 'text-muted-foreground' : 'text-faint-foreground',
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

/* ────────────────────────── Root ────────────────────────── */

type ItemElement = ReactElement<SegmentedButtonItemProps, typeof Item>;

interface SegmentedButtonProps {
  ariaLabel: string;
  children: ItemElement | ItemElement[];
}

/**
 * SegmentedButton — flat-raised segmented control matching the
 * mockup's `.seg` / `.seg-btn`. The container owns the muted track
 * background, hairline border and 2px inner padding; each `Item`
 * is a button that animates in/out of an inset shadow on activation.
 * Composes as a compound: `<SegmentedButton.Item>` children are the
 * only accepted shape.
 */
function SegmentedButtonRoot({
  ariaLabel,
  children,
}: Readonly<SegmentedButtonProps>): React.JSX.Element {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className="inline-flex gap-0 rounded-md border border-border-soft bg-muted p-0.5"
    >
      {children}
    </div>
  );
}

export const SegmentedButton = Object.assign(SegmentedButtonRoot, { Item });
